"use client";

import { ChannelAttributes } from "@/app/service";
import { CommentatorTileProps } from "@/app/types";
import { formatViewerHours } from "@/app/utils/timeUtils";
import { Pause, Play } from "lucide-react";
import Image from "next/image";
import ReactCountryFlag from "react-country-flag";
import IconButton from "../ui/IconButton/IconButton";
import { useState } from "react";

// Component for event-specific commentator tiles
export const EventCommentatorTile = (
    {
        attributes,
        onCommentator,
        setOnCommentator
    }: {
        attributes: ChannelAttributes;
        onCommentator: string | null;
        setOnCommentator: (key: string | null) => void;
    }
) => {
    const isActive = onCommentator === attributes.groupId;
    const hasCoCommentators = attributes.coCommentators && attributes.coCommentators.length > 0;
    const [showCoCommentators, setShowCoCommentators] = useState(false);

    const handleMainClick = () => {
        if (isActive) {
            setOnCommentator(null);
        } else {
            setOnCommentator(attributes.groupId);
        }
    };

    const handleCoCommentatorsClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering main click
        setShowCoCommentators(!showCoCommentators);
    };

    return (
        <div className="relative">
            <button
                onClick={handleMainClick}
                className={`
                    relative w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200
                    ${hasCoCommentators ? 'ring-2 ring-widertalk-pink/30' : ''}
                    ${isActive
                        ? 'bg-widertalk-navy shadow-lg'
                        : 'bg-widertalk-lime hover:bg-widertalk-lime/90'
                    }
                `}
            >
                {/* Live indicator */}
                {isActive && (
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-white font-medium">LIVE</span>
                    </div>
                )}

                {/* Avatar with flag */}
                <div className="relative flex-shrink-0">
                    {/* Only show avatar if avatarUrl exists and is not empty */}
                    {attributes.user.avatarUrl && attributes.user.avatarUrl.trim() !== "" ? (
                        <div className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${hasCoCommentators ? 'border-widertalk-navy' : 'border-gray-700'}`}>
                            <Image
                                src={attributes.user.avatarUrl}
                                alt={attributes.user.displayName || attributes.user.publicHandle || "User"}
                                width={48}
                                height={48}
                                className="object-cover"
                            />
                        </div>
                    ) : (
                        <div className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${hasCoCommentators ? 'border-widertalk-navy' : 'border-gray-700'} bg-gray-300 flex items-center justify-center`}>
                            <Image
                                src="/profile.svg"
                                alt={attributes.user.displayName || attributes.user.publicHandle || "User"}
                                width={48}
                                height={48}
                                className="object-cover"
                            />
                        </div>
                    )}
                    {/* Only show nationality flag if nationality exists and is not empty */}
                    {attributes.user.nationality && attributes.user.nationality.trim() !== "" && (
                        <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5">
                            <ReactCountryFlag
                                countryCode={attributes.user.nationality}
                                svg
                                style={{
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%'
                                }}
                                aria-label={attributes.user.nationality}
                            />
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 text-left">
                    <div className={`font-medium text-sm ${isActive ? 'text-white' : 'text-widertalk-navy'}`}>
                        {/* Use displayName if available and not empty, otherwise use publicHandle */}
                        {attributes.user.displayName && attributes.user.displayName.trim() !== ""
                            ? attributes.user.displayName
                            : attributes.user.publicHandle
                        }
                        {hasCoCommentators && (
                            <span className={`ml-2 text-xs ${isActive ? 'text-widertalk-lime' : 'text-widertalk-navy'} font-semibold`}>
                                • Team Commentary
                            </span>
                        )}
                    </div>
                    {/* Stats */}
                    <div className={`flex items-center gap-3 mt-1 rounded px-2 py-1 ${isActive ? 'bg-white/10 backdrop-blur-sm' : ''}`}>
                        <div className="flex items-center gap-1">
                            <Image
                                src="/viewers.svg"
                                alt="Viewers"
                                width={14}
                                height={14}
                                style={{ filter: isActive ? 'invert(1)' : 'invert(0.2)' }}
                            />
                            <span className={`text-xs ${isActive ? 'text-white' : 'text-widertalk-navy'}`}>
                                {attributes.liveViewerCount}
                            </span>
                        </div>
                        {/* Enhanced Co-commentators indicator - now more prominent */}
                        {hasCoCommentators && (
                            <button
                                onClick={handleCoCommentatorsClick}
                                className={`flex items-center gap-1 px-3 py-1 rounded-full transition-all duration-200 ${
                                    showCoCommentators 
                                        ? 'bg-widertalk-navy text-white shadow-md scale-105' 
                                        : (isActive ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-widertalk-navy/20 text-widertalk-navy hover:bg-widertalk-navy/30')
                                }`}
                                title={`View ${attributes.coCommentators.length} co-commentator${attributes.coCommentators.length > 1 ? 's' : ''}`}
                            >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-xs font-semibold">
                                    {attributes.coCommentators.length} Co-host{attributes.coCommentators.length > 1 ? 's' : ''}
                                </span>
                                <svg
                                    className={`w-3 h-3 transition-transform duration-200 ${showCoCommentators ? 'rotate-180' : ''}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Play/Pause icon */}
                <div className="flex-shrink-0">
                    {isActive ? (
                        <Pause className="w-5 h-5 text-white" />
                    ) : (
                        <Play className="w-5 h-5 text-widertalk-navy" />
                    )}
                </div>
            </button>

            {/* Co-commentators expandable section - enhanced styling */}
            {hasCoCommentators && (
                <div className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${showCoCommentators ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                `}>
                    <div className="bg-gradient-to-r from-widertalk-navy/10 to-widertalk-lime/10 border-l-4 border-widertalk-navy rounded-b-lg p-4 -mt-1 shadow-inner">
                        <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-widertalk-navy" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                            </svg>
                            <span className="text-sm font-bold text-widertalk-navy">
                                Team Commentary ({attributes.coCommentators.length} co-host{attributes.coCommentators.length > 1 ? 's' : ''})
                            </span>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            {attributes.coCommentators.map((coCommentator, index) => (
                                <div key={`${coCommentator.publicHandle}-${index}`} className="flex items-center gap-3 bg-white/60 rounded-lg p-3 shadow-sm">
                                    <div className="relative flex-shrink-0">
                                        {/* Only show avatar if avatarUrl exists and is not empty */}
                                        {coCommentator.avatarUrl && coCommentator.avatarUrl.trim() !== "" ? (
                                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-widertalk-navy/30">
                                                <Image
                                                    src={coCommentator.avatarUrl}
                                                    alt={coCommentator.displayName || coCommentator.publicHandle || "Co-commentator"}
                                                    width={40}
                                                    height={40}
                                                    className="object-cover"
                                                />
                                            </div>
                                        ) : (
                                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-widertalk-navy/30 bg-gray-300 flex items-center justify-center">
                                                <Image
                                                    src="/profile.svg"
                                                    alt={coCommentator.displayName || coCommentator.publicHandle || "Co-commentator"}
                                                    width={40}
                                                    height={40}
                                                    className="object-cover"
                                                />
                                            </div>
                                        )}
                                        {/* Only show nationality flag if nationality exists and is not empty */}
                                        {coCommentator.nationality && coCommentator.nationality.trim() !== "" && (
                                            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm">
                                                <ReactCountryFlag
                                                    countryCode={coCommentator.nationality}
                                                    svg
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%'
                                                    }}
                                                    aria-label={coCommentator.nationality}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-widertalk-navy truncate">
                                            {/* Use displayName if available and not empty, otherwise use publicHandle */}
                                            {coCommentator.displayName && coCommentator.displayName.trim() !== ""
                                                ? coCommentator.displayName
                                                : coCommentator.publicHandle
                                            }
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="bg-widertalk-navy/20 text-widertalk-navy px-2 py-1 rounded-full text-xs font-medium">
                                            Co-host
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Component for general commentator tiles
export const GeneralCommentatorTile = ({
    commentatorData,
    isActive = false,
    rank,
    onClick
}: CommentatorTileProps & {
    isActive?: boolean;
    rank?: number;
    onClick?: () => void;
}) => {
    const { displayName, nationality, avatarUrl, publicHandle, totalViewerHours, description } = commentatorData;

    return (
        <button
            onClick={onClick}
            className={`
                group relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2
                ${isActive 
                    ? 'bg-gradient-to-br from-widertalk-lime via-widertalk-lime/90 to-widertalk-lime/80 text-widertalk-navy border-2 border-widertalk-navy' 
                    : 'bg-gradient-to-br from-widertalk-navy via-widertalk-navy/95 to-widertalk-navy/90 text-white border border-gray-700/50'
                }
            `}
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/20 to-transparent rounded-full transform translate-x-16 -translate-y-16 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-white/10 to-transparent rounded-full transform -translate-x-12 translate-y-12 group-hover:scale-110 transition-transform duration-700"></div>
            </div>

            <div className="relative p-5">
                <div className="flex items-start gap-4">
                    {/* Enhanced Avatar Section */}
                    <div className="relative flex-shrink-0">
                        {/* Glowing ring animation */}
                        <div className={`
                            absolute inset-0 rounded-full transition-all duration-300 group-hover:animate-pulse
                            ${isActive 
                                ? 'ring-4 ring-widertalk-navy/30 ring-offset-2 ring-offset-widertalk-lime' 
                                : 'ring-4 ring-widertalk-lime/0 group-hover:ring-widertalk-lime/40 ring-offset-2 ring-offset-widertalk-navy'
                            }
                        `}></div>

                        {/* Avatar container with enhanced styling */}
                        <div className="relative w-16 h-16 rounded-full overflow-hidden border-3 border-white/20 shadow-lg">
                            {avatarUrl && avatarUrl.trim() !== "" ? (
                                <Image
                                    src={avatarUrl}
                                    alt={displayName || publicHandle || "User"}
                                    width={64}
                                    height={64}
                                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-110"
                                />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                                    <Image
                                        src="/profile.svg"
                                        alt={displayName || publicHandle || "User"}
                                        width={32}
                                        height={32}
                                        className="opacity-70"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Enhanced nationality flag */}
                        {nationality && nationality.trim() !== "" && (
                            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 shadow-lg border-2 border-white transform transition-transform duration-300 group-hover:scale-110">
                                <ReactCountryFlag
                                    className="emojiFlag"
                                    countryCode={nationality}
                                    svg
                                    style={{
                                        fontSize: "14px",
                                        lineHeight: "14px",
                                        borderRadius: "2px"
                                    }}
                                    aria-label={nationality}
                                />
                            </div>
                        )}
                    </div>

                    {/* Enhanced Content Section */}
                    <div className="flex-1 min-w-0">
                        {/* Name and handle */}
                        <div className="mb-2">
                            <h3 className={`
                                font-bold text-lg leading-tight transition-colors duration-300
                                ${isActive ? 'text-widertalk-navy' : 'text-white group-hover:text-widertalk-lime'}
                            `}>
                                {displayName && displayName.trim() !== "" ? displayName : publicHandle}
                            </h3>
                        </div>

                        {/* Enhanced Stats Row */}
                        <div className="flex items-center gap-4 mb-3">
                            {/* Total Hours */}
                            <div className={`
                                flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-300
                                ${isActive 
                                    ? 'bg-widertalk-navy/10 text-widertalk-navy' 
                                    : 'bg-white/10 text-white group-hover:bg-widertalk-lime/20 group-hover:text-widertalk-lime'
                                }
                            `}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm font-semibold">
                                    {formatViewerHours(totalViewerHours)}
                                </span>
                            </div>
                        </div>

                        {/* Description */}
                        {description && description.trim() !== "" && (
                            <p className={`
                                text-sm leading-relaxed line-clamp-2 transition-colors duration-300
                                ${isActive ? 'text-widertalk-navy/80' : 'text-white/80 group-hover:text-white'}
                            `}>
                                {description}
                            </p>
                        )}
                    </div>

                    {/* Enhanced Action Indicator */}
                    {/*<div className="flex-shrink-0 flex flex-col items-center gap-2">*/}
                    {/*    /!* Trending indicator *!/*/}
                    {/*    <div className={`*/}
                    {/*        w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 transform group-hover:rotate-12*/}
                    {/*        ${isActive */}
                    {/*            ? 'bg-widertalk-navy text-widertalk-lime' */}
                    {/*            : 'bg-widertalk-lime text-widertalk-navy group-hover:bg-white group-hover:scale-110'*/}
                    {/*        }*/}
                    {/*    `}>*/}
                    {/*        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                    {/*            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />*/}
                    {/*        </svg>*/}
                    {/*    </div>*/}

                    {/*</div>*/}
                </div>

                {/* Bottom gradient overlay for depth */}
                <div className={`
                    absolute bottom-0 left-0 right-0 h-1 transition-all duration-300
                    ${isActive 
                        ? 'bg-gradient-to-r from-widertalk-navy via-widertalk-navy/80 to-widertalk-navy' 
                        : 'bg-gradient-to-r from-widertalk-lime/0 via-widertalk-lime/50 to-widertalk-lime/0 group-hover:via-widertalk-lime'
                    }
                `}></div>
            </div>

            {/* Subtle shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out"></div>
        </button>
    );
};